import { useEffect, useMemo } from 'react';
import { CommonMainPanelForm } from '../../../components/AdminTemplates';
import Tabs from '../../../components/Tabs';
import { useLanguage } from '../../../contexts/language';
import { Operator } from '../../../types/operator';
import { isValidEmail, isValidWebUrl } from '../../../utils/validation';
import OperatorAddressForm from './Forms/OperatorAddressForm';
import OperatorBasicInformationForm from './Forms/OperatorBasicInformationForm';
import OperatorContactForm from './Forms/OperatorContactForm';
import useIotsAdminForm from '../../../hooks/useIotsAdminForm';
import { OperatorFormValues } from './types';

export interface OperatorFormProps {
  onNameChange?: (name: string) => void;
  onSubmit: (values: OperatorFormValues, callback: () => void) => void;
  onCancel?: () => void;
  isLoading: boolean;
  isEditingByDefault?: boolean;
  operator?: Operator | null;
}

const OperatorForm = ({
  onNameChange,
  onSubmit,
  onCancel,
  isLoading,
  isEditingByDefault = false,
  operator = null,
}: OperatorFormProps) => {
  const { t } = useLanguage();

  const areInputsDisabled =
    Boolean(operator && !operator.isActive) || isLoading;

  const initialValues = useMemo<OperatorFormValues>(() => {
    if (!operator) {
      return {
        companyName: '',
        website: '',
        trnVatNumber: '',
        tcanNumber: '',
        croNumber: '',
        addressLine1: '',
        addressLine2: '',
        addressLine3: '',
        county: '',
        eirCode: '',
        companyEmailAddress: '',
        companyPhoneNumber: '',
      };
    }
    return {
      companyName: operator.name,
      website: operator.webSite ?? '',
      trnVatNumber: operator.trnVatNumber,
      tcanNumber: operator.tcanNumber,
      croNumber: operator.croNumber,
      addressLine1: operator.addressLine1,
      addressLine2: operator.addressLine2,
      addressLine3: operator.addressLine3,
      county: operator.county,
      eirCode: operator.eircode,
      companyEmailAddress: operator.email,
      companyPhoneNumber: operator.phone,
    };
  }, [operator]);

  const formManager = useIotsAdminForm(
    {
      isEditingByDefault,
      initialValues,
      onSubmit: (values, callback) => {
        onSubmit(
          {
            companyName: values.companyName.trim(),
            website: values.website.trim(),
            trnVatNumber: values.trnVatNumber.trim(),
            tcanNumber: values.tcanNumber.trim(),
            croNumber: values.croNumber.trim(),
            addressLine1: values.addressLine1.trim(),
            addressLine2: values.addressLine2.trim(),
            addressLine3: values.addressLine3.trim(),
            county: values.county.trim(),
            eirCode: values.eirCode.trim(),
            companyEmailAddress: values.companyEmailAddress.trim(),
            companyPhoneNumber: values.companyPhoneNumber,
          },
          callback,
        );
      },
      onEditionCancel: onCancel,
      validate: (values, setError) => {
        if (!values.companyName.trim()) {
          setError('companyName', t('validation.required-field'));
        }
        if (!values.addressLine1.trim()) {
          setError('addressLine1', t('validation.required-field'));
        }

        if (!values.county.trim()) {
          setError('county', t('validation.invalid-option'));
        }
        if (!values.eirCode.trim()) {
          setError('eirCode', t('validation.required-field'));
        }
        if (!values.companyPhoneNumber) {
          setError('companyPhoneNumber', t('validation.required-field'));
        }
        if (!values.companyEmailAddress) {
          setError('companyEmailAddress', t('validation.required-field'));
        }
        if (!values.trnVatNumber) {
          setError('trnVatNumber', t('validation.required-field'));
        }
        if (!/^\d+$/.test(values.companyPhoneNumber.trim())) {
          setError('companyPhoneNumber', t('validation.invalid-phone-number'));
        }
        if (!isValidEmail(values.companyEmailAddress.trim())) {
          setError('companyEmailAddress', t('validation.invalid-email'));
        }
        const website = values.website.trim();
        if (website && !isValidWebUrl(website)) {
          setError('website', t('validation.invalid-website'));
        }
      },
      resetOnInitialValuesChange: () => !!operator,
    },
    { shouldInputsBeDisabled: areInputsDisabled },
  );

  const { formState } = formManager;
  const { errors } = formState;

  const isForm1Error = Boolean(
    errors.companyName ||
      errors.website ||
      errors.trnVatNumber ||
      errors.tcanNumber ||
      errors.croNumber,
  );
  const isForm2Error = Boolean(
    errors.addressLine1 ||
      errors.addressLine2 ||
      errors.addressLine3 ||
      errors.county ||
      errors.eirCode,
  );
  const isForm3Error = Boolean(
    errors.companyEmailAddress || errors.companyPhoneNumber,
  );

  useEffect(() => {
    onNameChange?.(formState.values.companyName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.values.companyName]);

  return (
    <CommonMainPanelForm
      isEditing={formState.isEditing}
      onSubmit={formState.handleSubmit}
      onCancelEdition={formState.cancelEdition}
      onEditionRequest={formState.requestEdition}
      isSubmitting={isLoading}
      isSubmitDisabled={
        areInputsDisabled ||
        !formState.haveValuesChanged ||
        formState.hasVisibleErrors
      }
      isEditDisabled={areInputsDisabled}
    >
      <Tabs
        tabs={[
          {
            label: t('content.basic-information'),
            icon: isForm1Error ? 'error' : undefined,
          },
          {
            label: t('content.address'),
            icon: isForm2Error ? 'error' : undefined,
          },
          {
            label: t('content.contact'),
            icon: isForm3Error ? 'error' : undefined,
          },
        ]}
      >
        <OperatorBasicInformationForm formManager={formManager} />
        <OperatorAddressForm formManager={formManager} />
        <OperatorContactForm formManager={formManager} />
      </Tabs>
    </CommonMainPanelForm>
  );
};

export default OperatorForm;
